// Turning a traced hole into a diagram.
//
// Pure and DOM-free so it can be unit tested; the component that draws the result is a
// thin shell over it. Everything here is flat-earth via `toLocalYards`, which is right
// at the scale of one golf hole and wrong at any larger one.
//
// The output is a plain vector plan — no imagery, no tiles, no network. Satellite is
// what the course was traced *from*; it is not what a player wants to read at a glance
// standing on a tee in the sun.
import type { CoursePath, CourseShape, HoleGeo, Landmark, LandmarkKind, LatLng, ShapeKind } from "./types.js";
import { backTeeCentroid, centroid, haversineYards, toLocalYards } from "./geo.js";

export interface PlanPoint {
  x: number;
  y: number;
}

export interface PlanShape {
  kind: ShapeKind;
  points: PlanPoint[];
  closed: boolean;
}

/**
 * The putting surface, drawn as a disc.
 *
 * A hole always gets one of these, because a hole diagram without a visible green
 * doesn't read as a golf hole at all. How honest it is depends on what was traced, and
 * the caller can tell from `measured`:
 *
 *  - a traced `green` outline wins and this is null — the real shape is drawn instead;
 *  - otherwise, if front and back are both set, the long axis is their *real* distance
 *    apart and only the width is assumed (`measured: true`);
 *  - with a center alone it is a nominal blob at a believable size (`measured: false`).
 *
 * Nothing reads this back. Distances come from the three points, never from here.
 */
export interface GreenDisc {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
  measured: boolean;
}

/**
 * A golf tee, drawn upright at the middle of a traced tee box.
 *
 * A pictograph rather than the pair of marker discs this used to draw. The discs had to
 * be laid out *across* the line of play, which meant deriving the box's long axis from
 * its own geometry — second moments integrated along the edges, so a side traced with
 * six clicks did not outvote one traced with two. All that to answer a question the
 * picture never actually asks: the plan is already rotated so the hole plays up the
 * page, so which way the box faces tells a player nothing they cannot see.
 *
 * A tee glyph says "you start here" with no direction at all, and reads at the size a
 * tee box occupies on a card — which the discs did not, being two dots a yard across on
 * a drawing two hundred yards wide.
 */
export interface TeeMarker {
  at: PlanPoint;
  /** Height of the glyph in SVG units, from the box it sits in. */
  size: number;
}

/** A cart path projected into the plan, with its width converted to SVG units. */
export interface PlanPath {
  points: PlanPoint[];
  widthUnits: number;
}

/**
 * Another hole of the same course, projected into this hole's frame.
 *
 * Drawn faintly underneath, and it is the difference between a picture of a hole and a
 * picture of *where you are*. A fairway on its own gives a player nothing to check
 * themselves against — every hole looks like this one — whereas the shape of the ground
 * next door is what you can actually see from where you're standing, and matching it to
 * the page is how you know the drawing is of the hole you're on.
 *
 * Carries no flag and no tee markers: exactly one hole on this page is the one being
 * played, and that has to be unambiguous at a glance.
 */
export interface PlanNeighbour {
  hole: number;
  shapes: PlanShape[];
  /** Where to write the hole number — its green — or null if that's off the frame. */
  label: PlanPoint | null;
}

/** A landmark projected into the plan. */
export interface PlanLandmark {
  kind: LandmarkKind;
  label: string;
  at: PlanPoint;
}

/** What a landmark is called when it hasn't been given its own label. */
export const LANDMARK_NAMES: Record<LandmarkKind, string> = {
  clubhouse: "Clubhouse",
  restroom: "Restroom",
  food: "Food",
  shop: "Pro shop",
  water: "Water",
  practice: "Practice",
  parking: "Parking",
  other: "Landmark",
};

/**
 * The course's hazards, split by how much they bear on the shot in front of you.
 *
 * A bunker or a lake belongs to the course rather than to a hole, so there is no
 * ownership to read off. What decides emphasis here is geometry: a hazard inside the
 * playing corridor is one you have to think about, and a hazard that merely reaches
 * into the frame is scenery.
 *
 * The corridor test used to run at import time, where a wrong guess was written into a
 * file and stayed there. Doing it here makes it styling — free to get wrong, and free
 * to change — and it lets one lake read as live on both the holes it really touches
 * instead of being filed under one of them.
 */
export interface PlanHazards {
  /** Within `HAZARD_LIVE_YARDS` of the playing line. Drawn at full strength. */
  near: PlanShape[];
  /** In the frame but outside the corridor. Drawn back, but not as far back as a neighbour. */
  far: PlanShape[];
}

export interface HolePlan {
  /** SVG user units; the viewBox is always 0 0 width height. */
  width: number;
  height: number;
  shapes: PlanShape[];
  /** Course-level bunkers and water that reach into this frame. */
  hazards: PlanHazards;
  /** Other holes that reach into this frame, for context. Empty unless asked for. */
  neighbours: PlanNeighbour[];
  /** One per traced tee box, in the same order as the `tee` shapes. */
  teeMarkers: TeeMarker[];
  /** Course-level cart paths, clipped by the viewport rather than framed. */
  paths: PlanPath[];
  /** Course-level landmarks, likewise clipped rather than framed. */
  landmarks: PlanLandmark[];
  greenFront: PlanPoint | null;
  greenCenter: PlanPoint | null;
  greenBack: PlanPoint | null;
  /** Null when a real green outline was traced — that shape is drawn instead. */
  greenDisc: GreenDisc | null;
  /** Yards per SVG unit, so a caller can place a scale bar or a distance arc. */
  yardsPerUnit: number;
  /**
   * Project any other coordinate into this same frame — a live GPS fix, most of the
   * time. Part of the plan rather than a separate call so a marker can never be drawn
   * against a different transform from the shapes underneath it.
   *
   * Points off the traced hole project outside the viewBox rather than being clamped;
   * the caller decides whether something 400 yards offline is worth drawing.
   */
  project(at: LatLng): PlanPoint;
}

/** Padding inside the viewBox so nothing is drawn hard against the edge. */
const PAD = 8;
/**
 * The longest side of the drawing, in SVG units. The other side follows from the hole's
 * real proportions rather than from a fixed frame.
 *
 * A hole is typically eight times longer than it is wide, and both axes must share one
 * scale or every distance in the picture would be a lie. Forcing that into a fixed
 * portrait box left the drawing as a thin ribbon with two thirds of the viewBox empty
 * on either side; sizing the box to the content means whatever height the page gives it
 * is spent on the hole.
 */
const LONG_SIDE = 200;

/**
 * Tallest the frame may be relative to its width, when the caller hasn't said what shape
 * box the plan is going into.
 *
 * A 400-yard hole 50 yards wide is 8:1, and drawn honestly in a phone-height box that
 * is a 40px ribbon in which a bunker is a smudge. Widening the frame past the traced
 * ground costs nothing and distorts nothing — both axes keep the same scale, there is
 * simply more empty grass either side — whereas stretching the hole to fit would make
 * every distance in the picture a lie.
 *
 * A caller that knows its box passes `aspect` and gets an exact fit instead; this is the
 * fallback for one that doesn't.
 */
const MAX_ASPECT = 3;

/** Grass to leave beyond the player and the green when framing on a live position. */
const CORRIDOR_MARGIN_YARDS = 30;

/**
 * How far off the playing line a course hazard may sit and still be drawn as live.
 *
 * The same 70 yards the importer used to use to decide *ownership*, kept because it was
 * tuned against real ground — generous across the hole, since that is the axis where
 * holes are actually separated, and a green complex with its bunkering is a good fifty
 * yards wide. The difference is that being wrong here costs a shade of grey rather than
 * a permanent line in a file.
 */
const HAZARD_LIVE_YARDS = 70;

/**
 * How far behind the tee a player can be and still count as playing the hole.
 *
 * Generous on purpose — waiting on the tee of the hole behind, or in the trees off the
 * previous fairway, is ordinary and should still frame the shot in front of you.
 */
const OFF_HOLE_MARGIN_YARDS = 150;

/**
 * Range to allow when the hole has no tee box to measure against.
 *
 * Comfortably past the longest hole anyone plays — championship par 5s stop around 700
 * yards and the longest in the world is under 1000.
 */
const UNKNOWN_HOLE_RANGE_YARDS = 1100;

/**
 * Whether a position is plausibly on this hole.
 *
 * Adaptive rather than a flat radius: the limit is the hole's own length plus a margin,
 * so a 150-yard par 3 isn't given the same allowance as a 600-yard par 5.
 *
 * Sitting at home the night before is the case this exists for. Without it, the framing
 * spans from the sofa to the golf course and the hole is a speck a few pixels across —
 * so being off the course produced a *worse* picture than having no position at all.
 */
export function isOnHole(geo: HoleGeo, at: LatLng): boolean {
  const green = geo.greenCenter ?? geo.greenFront ?? geo.greenBack;
  if (!green) return false;
  const tee = backTeeCentroid(geo);
  const limit = tee
    ? haversineYards(tee, green) + OFF_HOLE_MARGIN_YARDS
    : UNKNOWN_HOLE_RANGE_YARDS;
  return haversineYards(at, green) <= limit;
}

/**
 * Radius for a green known only by its center. A real green is nearer 15-20 yards
 * across, so this marks the spot at a believable size — it is not a measurement, and
 * nothing reads it back.
 */
const NOMINAL_GREEN_RADIUS_YARDS = 9;

/**
 * How wide a green is relative to its depth, when only the depth is known.
 *
 * Greens are usually a little wider than they are deep. Slightly under 1 keeps the
 * drawn shape from claiming more width than there is any evidence for.
 */
const GREEN_WIDTH_RATIO = 0.9;

function allPoints(geo: HoleGeo): LatLng[] {
  const out: LatLng[] = [];
  for (const p of [geo.greenFront, geo.greenCenter, geo.greenBack]) if (p) out.push(p);
  for (const s of geo.shapes ?? []) out.push(...s.points);
  return out;
}

/**
 * The direction the hole plays, as a bearing in radians from the tee toward the green.
 *
 * Derived from geometry rather than from any stored label. The tee *box* furthest from
 * the green gives the longest and most representative axis, and because tee sets are
 * deliberately not on the map, nothing here shifts when the club moves a marker.
 *
 * With no tee box traced, fall back to the longest axis of everything on the hole,
 * oriented so the green ends up at the top. That keeps a part-traced hole readable
 * instead of arbitrarily rotated.
 */
function playingAngle(geo: HoleGeo, origin: LatLng): number | null {
  const green = geo.greenCenter ?? geo.greenFront ?? geo.greenBack;
  if (!green) return null;
  const g = toLocalYards(origin, green);

  const tee = backTeeCentroid(geo) ?? impliedTee(geo);
  if (tee) {
    const t = toLocalYards(origin, tee);
    const dx = g.x - t.x;
    const dy = g.y - t.y;
    if (dx !== 0 || dy !== 0) return Math.atan2(dy, dx);
  }
  return null;
}

/**
 * The furthest traced point from the green, used as a stand-in tee when no box exists.
 *
 * On a hole with a fairway but no tee box drawn, the far end of the fairway is a much
 * better guess at "where this is played from" than the bounding box's major axis.
 */
function impliedTee(geo: HoleGeo): LatLng | null {
  const green = geo.greenCenter ?? geo.greenFront ?? geo.greenBack;
  if (!green) return null;
  let furthest: LatLng | null = null;
  let best = 0;
  for (const shape of geo.shapes ?? []) {
    // Both of these sit *at* the green, so neither says anything about where the hole
    // is played from.
    if (shape.kind === "green" || shape.kind === "apron") continue;
    const c = centroid(shape.points.length ? shape.points : [green]);
    const { x, y } = toLocalYards(green, c);
    const d = x * x + y * y;
    if (d > best) {
      best = d;
      furthest = c;
    }
  }
  return furthest;
}

/**
 * Project one hole into a portrait viewBox with the green at the top.
 *
 * Returns null when there is nothing to draw — an untraced hole renders as nothing at
 * all rather than as an empty frame.
 */
/** Cart paths are about three yards across. */
const DEFAULT_PATH_WIDTH_YARDS = 3;

export function holePlan(
  geo: HoleGeo,
  opts: {
    from?: LatLng | null;
    paths?: CoursePath[];
    landmarks?: Landmark[];
    /**
     * Width divided by height of the box this will be drawn into, when the caller has
     * measured it.
     *
     * The frame is grown on whichever axis is short until it is that shape, so
     * `preserveAspectRatio` has nothing left to letterbox. Without it a portrait hole in
     * a landscape card is fitted to the height and the drawing ends up a narrow strip
     * down the middle with dead bars either side — and since everything is clipped to
     * the frame, those bars are where a cart path or a landmark label gets cut in half
     * at an edge nobody can see.
     *
     * Growing costs nothing: both axes keep the same scale, so nothing moves and nothing
     * changes size on screen — the bars simply fill with the ground that was always
     * there.
     */
    aspect?: number | null;
    /**
     * The rest of the course's holes, for the faint context layer.
     *
     * Same rule as paths and landmarks: projected after the frame is fixed and never
     * fed into it. The hole in hand decides what the picture is of; the hole alongside
     * only gets drawn if it happens to reach in.
     */
    others?: HoleGeo[];
    /**
     * The course's bunkers and water — `CourseMap.shapes`.
     *
     * Passed whole rather than pre-filtered: which of them belong in this hole's picture
     * is a question about this frame, and the frame isn't known until it has been built.
     */
    hazards?: CourseShape[];
  } = {},
): HolePlan | null {
  const points = allPoints(geo);
  if (points.length === 0) return null;

  const origin = geo.greenCenter ?? points[0]!;
  const angle = playingAngle(geo, origin);

  // Rotate so the tee→green axis points up the page (+y in SVG is down, so the green
  // ending at a small y is what "up" means once the flip below is applied).
  const rot = angle === null ? 0 : Math.PI / 2 - angle;
  const cos = Math.cos(rot);
  const sin = Math.sin(rot);

  const project = (p: LatLng): PlanPoint => {
    const { x, y } = toLocalYards(origin, p);
    return { x: x * cos - y * sin, y: x * sin + y * cos };
  };

  const projected = points.map(project);

  /**
   * What to frame.
   *
   * With a live position on the hole, only the corridor between the player and the
   * green: the tee box is of no interest from 180 yards out, and including it shrinks
   * everything that is.
   *
   * Otherwise the whole hole, tee included — which is both what you want with no
   * position at all and what you want when the position is nowhere near. A player at
   * home the night before is looking at the hole to see it, not to be told how far away
   * their sofa is.
   */
  let framed = projected;
  if (opts.from && isOnHole(geo, opts.from)) {
    const me = project(opts.from);
    const green = geo.greenCenter ? project(geo.greenCenter) : null;
    if (green) {
      const lo = Math.min(me.y, green.y) - CORRIDOR_MARGIN_YARDS;
      const hi = Math.max(me.y, green.y) + CORRIDOR_MARGIN_YARDS;
      const inCorridor = projected.filter((p) => p.y >= lo && p.y <= hi);
      framed = [me, green, ...inCorridor];
    }
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of framed) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  }

  let spanX = Math.max(maxX - minX, 1);
  const spanY = Math.max(maxY - minY, 1);

  // Widen rather than squash: the scale stays shared, so distances stay true.
  const minSpanX = spanY / MAX_ASPECT;
  if (spanX < minSpanX) {
    const grow = (minSpanX - spanX) / 2;
    minX -= grow;
    maxX += grow;
    spanX = minSpanX;
  }
  // One scale for both axes: a hole squashed to fill the frame would misrepresent every
  // distance on it, and this diagram sits next to real yardages.
  const scale = LONG_SIDE / Math.max(spanX, spanY);

  let width = spanX * scale + PAD * 2;
  let height = spanY * scale + PAD * 2;

  // Grow the short axis until the frame is the shape of the box it's going into, so the
  // drawing fills it instead of being letterboxed into a strip. Padding is already in
  // `width`/`height`, which is why this works on the drawn size rather than the spans.
  //
  // The scale is untouched, so this is not a zoom: the same hole renders at the same
  // size in the same place, and the extra frame is extra ground.
  const aspect = opts.aspect && Number.isFinite(opts.aspect) && opts.aspect > 0 ? opts.aspect : null;
  if (aspect) {
    if (width < height * aspect) {
      const grow = (height * aspect - width) / 2 / scale;
      minX -= grow;
      maxX += grow;
      width = height * aspect;
    } else if (height < width / aspect) {
      const grow = (width / aspect - height) / 2 / scale;
      minY -= grow;
      maxY += grow;
      height = width / aspect;
    }
  }

  // The y flip is what puts the green at the top: local yards count north as positive,
  // SVG counts down as positive.
  const place = (p: LatLng): PlanPoint => {
    const q = project(p);
    return {
      x: PAD + (q.x - minX) * scale,
      y: height - (PAD + (q.y - minY) * scale),
    };
  };

  const shapes: PlanShape[] = (geo.shapes ?? []).map((s: CourseShape) => ({
    kind: s.kind,
    points: s.points.map(place),
    closed: s.kind !== "path",
  }));

  // The playing line, in plan units, for deciding which hazards are live. The tee end
  // falls back to the bottom of the frame: with no tee box traced there is still a
  // direction of play — it is the axis the whole drawing was rotated onto — and a
  // hazard beside the fairway should read as live whether or not anyone drew the tee.
  const greenPlan = geo.greenCenter ? place(geo.greenCenter) : null;
  const teeLatLng = backTeeCentroid(geo);
  const teePlan = teeLatLng ? place(teeLatLng) : greenPlan ? { x: greenPlan.x, y: height } : null;

  // Deliberately projected *after* the frame is fixed and never fed into the bounding
  // box, exactly like paths and landmarks. A lake at the far end of the property must
  // not zoom this hole out to include it.
  const hazards: PlanHazards = { near: [], far: [] };
  for (const s of opts.hazards ?? []) {
    if (s.points.length < 3) continue;
    const points = s.points.map(place);
    if (!overlapsFrame(points, width, height)) continue;
    const shape: PlanShape = { kind: s.kind, points, closed: true };
    // Measured from the shape's nearest point rather than its centre: a lake can be
    // enormous and 700 yards from the line by its middle while lapping the fairway.
    const liveUnits = HAZARD_LIVE_YARDS * scale;
    const live =
      greenPlan && teePlan
        ? points.some((p) => distanceToSegment(p, teePlan, greenPlan) <= liveUnits)
        : true;
    (live ? hazards.near : hazards.far).push(shape);
  }

  // Deliberately projected *after* the frame is fixed and never fed into the bounding
  // box. A cart path runs the length of the course, so framing on it would zoom the
  // plan out to the whole property; the viewport clips whatever runs off the edge.
  const planPaths: PlanPath[] = (opts.paths ?? [])
    .filter((p) => p.points.length >= 2)
    .map((p) => ({
      points: p.points.map(place),
      widthUnits: (p.widthYards ?? DEFAULT_PATH_WIDTH_YARDS) * scale,
    }));

  // Same rule as paths: projected after the frame is fixed, never fed into it. A
  // clubhouse half a mile away must not zoom the hole out to include it.
  const planLandmarks: PlanLandmark[] = (opts.landmarks ?? []).map((l) => ({
    kind: l.kind,
    label: l.label?.trim() || LANDMARK_NAMES[l.kind],
    at: place(l.at),
  }));

  // Likewise projected after the fact. A hole is dropped entirely unless something of
  // it overlaps the frame, so the common case — the far side of the course — costs a
  // bounding box per shape and nothing else.
  const neighbours: PlanNeighbour[] = [];
  for (const other of opts.others ?? []) {
    if (other.hole === geo.hole) continue;
    const near: PlanShape[] = [];
    for (const s of other.shapes ?? []) {
      if (s.points.length < 2) continue;
      const points = s.points.map(place);
      if (overlapsFrame(points, width, height)) {
        near.push({ kind: s.kind, points, closed: s.kind !== "path" });
      }
    }
    if (near.length === 0) continue;
    const green = other.greenCenter ?? other.greenFront ?? other.greenBack;
    const at = green ? place(green) : null;
    neighbours.push({
      hole: other.hole,
      shapes: near,
      label: at && at.x >= 0 && at.x <= width && at.y >= 0 && at.y <= height ? at : null,
    });
  }

  const greenFront = geo.greenFront ? place(geo.greenFront) : null;
  const greenCenter = geo.greenCenter ? place(geo.greenCenter) : null;
  const greenBack = geo.greenBack ? place(geo.greenBack) : null;

  return {
    width,
    height,
    shapes,
    hazards,
    neighbours,
    teeMarkers: teeMarkersFor(shapes),
    paths: planPaths,
    landmarks: planLandmarks,
    greenFront,
    greenCenter,
    greenBack,
    greenDisc: greenDiscFor(shapes, greenFront, greenCenter, greenBack, scale),
    yardsPerUnit: 1 / scale,
    project: place,
  };
}

/**
 * How tall a tee glyph is drawn, against a 200-unit long side.
 *
 * Clamped rather than scaled freely: a championship tee and a forward tee differ by more
 * on the ground than they should on a card, and a glyph that grows with its box makes
 * the back tee look like a different kind of thing.
 */
const TEE_GLYPH_MIN = 3.4;
const TEE_GLYPH_MAX = 6.5;
/** Multiple of the room inside the box the glyph aims for, before clamping. */
const TEE_GLYPH_SPAN = 1.6;

function teeMarkersFor(shapes: PlanShape[]): TeeMarker[] {
  const markers: TeeMarker[] = [];
  for (const s of shapes) {
    if (s.kind !== "tee" || s.points.length < 3) continue;

    let cx = 0;
    let cy = 0;
    for (const p of s.points) {
      cx += p.x;
      cy += p.y;
    }
    cx /= s.points.length;
    cy /= s.points.length;

    // How much room there is inside the box: the nearest edge to its middle. Measured to
    // the *edges* rather than to the corners, and therefore the same however the box is
    // turned on the page — which is the whole point of a glyph that carries no
    // direction. A bounding box would not be: a rectangle rotated forty degrees has a
    // bigger one than the same rectangle square to the page, and the glyph would grow
    // for no reason a reader could see.
    let reach = Infinity;
    for (let i = 0; i < s.points.length; i++) {
      const a = s.points[i]!;
      const b = s.points[(i + 1) % s.points.length]!;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len2 = dx * dx + dy * dy;
      if (len2 === 0) continue;
      const t = Math.max(0, Math.min(1, ((cx - a.x) * dx + (cy - a.y) * dy) / len2));
      reach = Math.min(reach, Math.hypot(cx - (a.x + t * dx), cy - (a.y + t * dy)));
    }
    if (!Number.isFinite(reach)) continue;
    const size = Math.min(Math.max(reach * TEE_GLYPH_SPAN, TEE_GLYPH_MIN), TEE_GLYPH_MAX);

    markers.push({ at: { x: cx, y: cy }, size });
  }
  return markers;
}

/**
 * The outline of a golf tee, as an SVG path, centred on a point and drawn upright.
 *
 * Upright always. The plan is rotated so the hole plays up the page, so there is no
 * orientation left for this to carry — and a pictograph that tilts reads as a mistake
 * rather than as information.
 */
export function teeGlyphPath(m: TeeMarker): string {
  const { x, y } = m.at;
  const s = m.size;
  // A wide shallow cup, a flare under it, a pinched neck, then a long taper to a point.
  //
  // The neck is what makes this read. An earlier version ran the cup straight down to
  // the tip and came out as a plain downward triangle at card size — the silhouette of
  // a golf tee is the *waist*, not the taper, and without it the glyph said "arrow".
  const p = (dx: number, dy: number) => `${(x + dx * s).toFixed(2)},${(y + dy * s).toFixed(2)}`;
  return [
    `M${p(-0.5, -0.58)}`,
    `L${p(0.5, -0.58)}`,
    `L${p(0.3, -0.3)}`,
    `L${p(0.11, -0.16)}`,
    `L${p(0.07, 0.25)}`,
    `L${p(0, 0.62)}`,
    `L${p(-0.07, 0.25)}`,
    `L${p(-0.11, -0.16)}`,
    `L${p(-0.3, -0.3)}`,
    "Z",
  ].join("");
}

function greenDiscFor(
  shapes: PlanShape[],
  front: PlanPoint | null,
  center: PlanPoint | null,
  back: PlanPoint | null,
  scale: number,
): GreenDisc | null {
  // A traced outline is the real thing and beats anything derived.
  if (shapes.some((s) => s.kind === "green" && s.points.length >= 3)) return null;
  if (!center) return null;

  if (front && back) {
    // The plan is rotated so the hole plays bottom-to-top, which puts front and back on
    // the vertical axis — so their separation on the page is the green's real depth.
    const depth = Math.hypot(back.x - front.x, back.y - front.y);
    const ry = Math.max(depth / 2, 1);
    return {
      cx: (front.x + back.x) / 2,
      cy: (front.y + back.y) / 2,
      rx: ry * GREEN_WIDTH_RATIO,
      ry,
      measured: true,
    };
  }

  const r = NOMINAL_GREEN_RADIUS_YARDS * scale;
  return { cx: center.x, cy: center.y, rx: r, ry: r, measured: false };
}

/**
 * Whether a projected outline reaches the frame at all.
 *
 * Bounding boxes rather than a real intersection: the answer only decides whether to
 * hand the shape to a clip that would have discarded it anyway, so the cost of being
 * generous is one invisible path element and the cost of being clever is a polygon
 * clipper nobody needs.
 */
/**
 * Distance from a point to a line segment, in whatever units went in.
 *
 * A segment rather than an infinite line: the playing line stops at the tee and at the
 * green, and a bunker two hundred yards behind the tee is not beside the hole however
 * neatly it lines up with it.
 */
function distanceToSegment(p: PlanPoint, a: PlanPoint, b: PlanPoint): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

function overlapsFrame(points: PlanPoint[], width: number, height: number): boolean {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  }
  return maxX >= 0 && minX <= width && maxY >= 0 && minY <= height;
}

/** Whether a projected point is inside the drawn frame, for deciding to render it. */
export function isOnPlan(plan: HolePlan, p: PlanPoint): boolean {
  return p.x >= 0 && p.x <= plan.width && p.y >= 0 && p.y <= plan.height;
}

/**
 * How round the curve through the traced points is. 1/6 is the standard Catmull-Rom
 * conversion and follows the points closely; lower would flatten toward the polygon it
 * came from, higher overshoots into loops on tight corners.
 */
const SMOOTHING = 1 / 6;

function fmt(n: number): string {
  return (Math.round(n * 10) / 10).toString();
}

/**
 * An SVG path through the points, as a smooth curve rather than straight segments.
 *
 * Nothing on a golf course has corners. A bunker traced with eight clicks and drawn as
 * an octagon reads as a diagram of a bunker; the same eight points drawn as a curve
 * read as a bunker. This is a Catmull-Rom spline converted to cubic béziers, which
 * passes exactly through every point that was clicked — so the shape is still the
 * traced shape, just without the corners the tracing method introduced.
 *
 * Falls back to straight lines below three points, where there is no curve to fit.
 */
export function smoothPath(points: PlanPoint[], closed: boolean): string {
  if (points.length === 0) return "";
  if (points.length < 3) {
    return points.map((p, i) => `${i === 0 ? "M" : "L"}${fmt(p.x)},${fmt(p.y)}`).join(" ");
  }

  const n = points.length;
  // A closed ring wraps at both ends so the join at the start point is as smooth as
  // every other; an open line clamps to its endpoints instead.
  const at = (i: number): PlanPoint =>
    closed
      ? points[((i % n) + n) % n]!
      : points[Math.max(0, Math.min(n - 1, i))]!;

  const segments = closed ? n : n - 1;
  let d = `M${fmt(points[0]!.x)},${fmt(points[0]!.y)}`;

  for (let i = 0; i < segments; i++) {
    const p0 = at(i - 1);
    const p1 = at(i);
    const p2 = at(i + 1);
    const p3 = at(i + 2);

    const c1x = p1.x + (p2.x - p0.x) * SMOOTHING;
    const c1y = p1.y + (p2.y - p0.y) * SMOOTHING;
    const c2x = p2.x - (p3.x - p1.x) * SMOOTHING;
    const c2y = p2.y - (p3.y - p1.y) * SMOOTHING;

    d += ` C${fmt(c1x)},${fmt(c1y)} ${fmt(c2x)},${fmt(c2y)} ${fmt(p2.x)},${fmt(p2.y)}`;
  }

  return closed ? `${d} Z` : d;
}
